import React, { useState, useRef, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import { formatUsd } from '../../utils/agentCost';
import { useChatDock } from '../../contexts/ChatDockContext';
import BuzzAI from '../BuzzAI';
import { dataService } from '../../services/dataService';

/**
 * One conversation, docked at the bottom of the screen.
 *
 * Deliberately small and fixed: the point is to be able to ask an agent
 * something without losing the page you were reading, so it must never take
 * the screen over.
 */
function ChatWindow({ chat, onClose, onToggle }) {
  const { agent, fnNode, workspaceId } = chat;
  const [messages, setMessages] = useState([]);
  const [draft, setDraft]       = useState('');
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');
  const [spent, setSpent]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const endRef = useRef(null);

  // The conversation lives server-side; the window just has to ask for it.
  // Without this, closing the window or signing out looked like the history had
  // been discarded — while the agent could still see all of it.
  useEffect(() => {
    let alive = true;
    httpsCallable(functions, 'agentChatHistoryFn')({
      workspaceId, functionNodeId: fnNode.id, agentId: agent.id,
    }).then(res => {
      if (!alive) return;
      const d = res?.data || {};
      if (Array.isArray(d.messages) && d.messages.length) setMessages(d.messages);
      if (d.usdCost !== undefined && d.usdCost !== null) setSpent(d.usdCost);
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [workspaceId, fnNode.id, agent.id]);

  useEffect(() => {
    if (!chat.minimised) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, busy, chat.minimised]);

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft(''); setError('');
    setMessages(m => [...m, { who: 'you', text }]);
    setBusy(true);
    try {
      const res = await httpsCallable(functions, 'askAgentDirect')({
        workspaceId, functionNodeId: fnNode.id, agentId: agent.id, message: text,
      });
      const d = res?.data || {};
      if (!d.success) setError(d.error || 'That did not work.');
      else {
        setMessages(m => [...m, { who: 'agent', text: d.reply }]);
        if (d.usdCost !== undefined && d.usdCost !== null) setSpent(d.usdCost);
      }
    } catch (e) {
      setError(e.message || 'Could not reach the agent.');
    }
    setBusy(false);
  };

  return (
    <div className={`chatdock-window${chat.minimised ? ' minimised' : ''}`}>
      <div className="chatdock-bar" onClick={onToggle}>
        <span className="chatdock-title">
          <span className="chatdock-icon">🤖</span>
          <span className={`chatdock-dot${busy ? ' busy' : ''}`} />
          {agent.name || 'Agent'}
          <span className="chatdock-team">{fnNode.data?.label || ''}</span>
        </span>
        <span className="chatdock-bar-actions">
          {spent !== null && <span className="chatdock-cost">{formatUsd(spent)}</span>}
          <button
            className="chatdock-btn"
            title={chat.minimised ? 'Open' : 'Minimise'}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
          >{chat.minimised ? '▴' : '▾'}</button>
          <button
            className="chatdock-btn"
            title="Close"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
          >×</button>
        </span>
      </div>

      {!chat.minimised && (
        <>
          <div className="chatdock-log">
            {loading && messages.length === 0 && (
              <div className="chatdock-hint">Loading your conversation…</div>
            )}
            {!loading && messages.length === 0 && !busy && (
              <div className="chatdock-hint">
                Ask about the work it has done or the knowledge it was given —
                “how have the tickets been today?”, “what keeps coming up?”.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`chatdock-msg ${m.who}`}>
                <div className="chatdock-who">{m.who === 'you' ? 'You' : (agent.name || 'Agent')}</div>
                <div className="chatdock-text">{m.text}</div>
              </div>
            ))}
            {busy && (
              <div className="chatdock-msg agent">
                <div className="chatdock-who">{agent.name || 'Agent'}</div>
                <div className="chatdock-text chatdock-thinking">thinking…</div>
              </div>
            )}
            {error && <div className="chatdock-error">{error}</div>}
            <div ref={endRef} />
          </div>

          <div className="chatdock-input">
            <textarea
              rows={1}
              value={draft}
              placeholder={`Ask ${agent.name || 'this agent'}…`}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
            />
            <button onClick={send} disabled={busy || !draft.trim()}>
              {busy ? '…' : 'Send'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Buzz in the same dock as the agents.
 *
 * It used to be a separate draggable panel with its own chrome, which made it
 * feel like a different kind of thing from the agents — it is not, it is just
 * the one that knows about the pipeline rather than a job. Distinct icon so it
 * is still obvious which is which.
 */
function BuzzWindow({ chat, onClose, onToggle }) {
  return (
    <div className={`chatdock-window buzz${chat.minimised ? ' minimised' : ''}`}>
      <div className="chatdock-bar" onClick={onToggle}>
        <span className="chatdock-title">
          <span className="chatdock-icon">🐝</span>
          Buzz AI
          <span className="chatdock-team">pipeline assistant</span>
        </span>
        <span className="chatdock-bar-actions">
          <button className="chatdock-btn" title={chat.minimised ? 'Open' : 'Minimise'}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}>{chat.minimised ? '▴' : '▾'}</button>
          <button className="chatdock-btn" title="Close"
            onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>
        </span>
      </div>
      {!chat.minimised && (
        <BuzzAI docked workspaceId={chat.workspaceId} user={chat.user} onClose={onClose} />
      )}
    </div>
  );
}

/**
 * The list of who you can talk to.
 *
 * Docked windows alone do not scale: with more than a couple open you cannot
 * see what else exists, and closing one loses the way back to it. This is the
 * roster — every agent across every team, plus Buzz — so the dock holds the
 * conversations you are having and this holds the ones you could have.
 */
function ChatLauncher({ workspaceId, user }) {
  const { chats, openChat } = useChatDock();
  const [open, setOpen] = useState(false);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (!workspaceId) { setTeams([]); return; }
    let alive = true;
    dataService.getPipeline(workspaceId).then(p => {
      if (!alive) return;
      setTeams((p?.nodes || []).filter(n => n.type === 'functionNode'));
    }).catch(() => {});
    return () => { alive = false; };
  }, [workspaceId]);

  const isOpen = (id) => chats.some(c => c.id === id);
  const totalAgents = teams.reduce((n, t) => n + (t.data?.agents || []).length, 0);

  return (
    <div className="chatdock-launcher">
      {open && (
        <div className="chatdock-list">
          <div className="chatdock-list-head">
            <span>Chats</span>
            <button className="chatdock-btn" aria-label="Close" title="Close" onClick={() => setOpen(false)}>×</button>
          </div>

          <button
            className={`chatdock-list-item${isOpen('buzz') ? ' active' : ''}`}
            onClick={() => { openChat({ id: 'buzz', kind: 'buzz', workspaceId, user }); setOpen(false); }}
          >
            <span className="chatdock-icon">🐝</span>
            <span className="chatdock-list-name">Buzz AI</span>
            <span className="chatdock-list-sub">pipeline assistant</span>
          </button>

          {totalAgents === 0 && (
            <div className="chatdock-list-empty">
              No agents yet. Add one to a Function node and it will appear here.
            </div>
          )}

          {teams.map(t => (
            (t.data?.agents || []).length > 0 && (
              <div key={t.id}>
                <div className="chatdock-list-team">{t.data?.label || t.id}</div>
                {(t.data.agents || []).map(a => (
                  <button
                    key={a.id}
                    className={`chatdock-list-item${isOpen(`${t.id}__${a.id}`) ? ' active' : ''}`}
                    onClick={() => {
                      openChat({ id: `${t.id}__${a.id}`, agent: a, fnNode: t, workspaceId });
                      setOpen(false);
                    }}
                  >
                    <span className="chatdock-icon">🤖</span>
                    <span className="chatdock-list-name">{a.name || 'Agent'}</span>
                    {a.role && <span className="chatdock-list-sub">{a.role}</span>}
                  </button>
                ))}
              </div>
            )
          ))}
        </div>
      )}

      <button className="chatdock-launch-btn" onClick={() => setOpen(o => !o)} title="Chats">
        💬 <span className="chatdock-launch-label">Chats</span>
        {chats.length > 0 && <span className="chatdock-launch-count">{chats.length}</span>}
      </button>
    </div>
  );
}

export default function ChatDock({ workspaceId, user }) {
  const { chats, closeChat, toggleMinimise } = useChatDock();
  // Always rendered: the launcher is how a conversation is started, so hiding
  // the dock when nothing is open would hide the way in.
  // Buzz first so it keeps a predictable spot as agent conversations come and go.
  const ordered = [...chats].sort((a, b) => (a.kind === 'buzz' ? -1 : b.kind === 'buzz' ? 1 : 0));
  return (
    <div className="chatdock">
      {ordered.map(c => c.kind === 'buzz' ? (
        <BuzzWindow key={c.id} chat={c}
          onClose={() => closeChat(c.id)} onToggle={() => toggleMinimise(c.id)} />
      ) : (
        <ChatWindow key={c.id} chat={c}
          onClose={() => closeChat(c.id)} onToggle={() => toggleMinimise(c.id)} />
      ))}
      <ChatLauncher workspaceId={workspaceId} user={user} />
    </div>
  );
}
